import React, { useCallback, useEffect, useRef } from "react";

export interface FirmaPadProps {
	/** PNG data URL of the signature, or null when the pad is empty. */
	value: string | null;
	onChange: (dataUrl: string | null) => void;
	disabled?: boolean;
	/** Caption under the signature line: "Firma de conformidad · {studentName}". */
	studentName?: string | null;
	/** CSS height of the pad (default 170 px). */
	height?: number;
}

/** Ink the student must leave before the signature counts (CSS px of stroke length): a tap or a dot is not a signature. */
const MIN_INK = 40;
/** Signature ink: the brand blue. */
const INK = "#212070";
/** The pad bitmap is "paper": white in both themes so the exported PNG (and the PDF) always reads. Not a UI surface colour. */
const PAPER = "#ffffff";

/**
 * Hand signature pad for the delivery receipt (Control de Entregas, Oct 2026). Pointer Events (finger, mouse, pen),
 * crisp on retina, does not scroll the page while drawing (touch-action: none). Exports PNG via onChange once the
 * stroke is long enough to be a signature; onChange(null) when cleared.
 */
const FirmaPad: React.FC<FirmaPadProps> = ({ value, onChange, disabled, studentName, height = 170 }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const stroke = useRef<{ x: number; y: number } | null>(null);
	const ink = useRef<number>(0);
	const hasValue = useRef<boolean>(!!value);
	const lastEmitted = useRef<string | null>(value);

	/** Sets the backing store to the CSS size × devicePixelRatio and paints the paper; optionally redraws a previous PNG. */
	const prepare = useCallback((redraw: string | null) => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const rect = canvas.getBoundingClientRect();
		if (rect.width < 1) return;
		const ratio = Math.min(window.devicePixelRatio || 1, 3);
		canvas.width = Math.round(rect.width * ratio);
		canvas.height = Math.round(rect.height * ratio);
		const context = canvas.getContext("2d");
		if (!context) return;
		context.setTransform(1, 0, 0, 1, 0, 0);
		context.scale(ratio, ratio);
		context.fillStyle = PAPER;
		context.fillRect(0, 0, rect.width, rect.height);
		context.lineWidth = 2.4;
		context.lineCap = "round";
		context.lineJoin = "round";
		context.strokeStyle = INK;
		if (redraw) {
			const image = new Image();
			image.onload = () => { context.drawImage(image, 0, 0, rect.width, rect.height); };
			image.src = redraw;
		}
	}, []);

	// Mount: blank paper (or the given signature). Resize (rotation, split view): keep what was drawn.
	useEffect(() => {
		prepare(value);
		const onResize = () => prepare(hasValue.current && canvasRef.current ? canvasRef.current.toDataURL("image/png") : null);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prepare]);

	// Parent reset (value back to null after we emitted one): wipe the paper.
	useEffect(() => {
		if (value === null && lastEmitted.current !== null) {
			lastEmitted.current = null;
			hasValue.current = false;
			ink.current = 0;
			prepare(null);
		}
	}, [value, prepare]);

	const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
		const rect = event.currentTarget.getBoundingClientRect();
		return { x: event.clientX - rect.left, y: event.clientY - rect.top };
	};

	const down = (event: React.PointerEvent<HTMLCanvasElement>) => {
		if (disabled) return;
		event.preventDefault();
		event.currentTarget.setPointerCapture(event.pointerId);
		stroke.current = point(event);
	};

	const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
		if (!stroke.current || disabled) return;
		event.preventDefault();
		const context = event.currentTarget.getContext("2d");
		if (!context) return;
		const next = point(event);
		context.beginPath();
		context.moveTo(stroke.current.x, stroke.current.y);
		context.lineTo(next.x, next.y);
		context.stroke();
		ink.current += Math.hypot(next.x - stroke.current.x, next.y - stroke.current.y);
		stroke.current = next;
	};

	const up = (event: React.PointerEvent<HTMLCanvasElement>) => {
		if (!stroke.current) return;
		stroke.current = null;
		if (ink.current < MIN_INK) return;
		const dataUrl = event.currentTarget.toDataURL("image/png");
		hasValue.current = true;
		lastEmitted.current = dataUrl;
		onChange(dataUrl);
	};

	const clear = () => {
		if (disabled) return;
		ink.current = 0;
		hasValue.current = false;
		lastEmitted.current = null;
		prepare(null);
		onChange(null);
	};

	return <div style={{ opacity: disabled ? 0.6 : 1 }}>
		<canvas ref={canvasRef} aria-label="Recuadro para firmar"
			onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
			style={{ display: "block", width: "100%", height: height + "px", borderRadius: "10px", border: "1px solid var(--wa-filo-2)", background: PAPER, touchAction: "none", cursor: disabled ? "default" : "crosshair" }} />
		<div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginTop: "6px" }}>
			<span style={{ flex: 1, minWidth: 0, borderTop: "1px solid var(--wa-filo-2)", paddingTop: "4px", fontSize: "12px", color: "var(--wa-apagado)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
				Firma de conformidad{studentName ? " · " + studentName : ""}
			</span>
			<button type="button" className="btn btn-link btn-sm" disabled={disabled || !value} onClick={clear}
				style={{ padding: "0 2px", fontSize: "13px", textDecoration: "none", lineHeight: 1.4 }}>Borrar</button>
		</div>
	</div>;
};

export default FirmaPad;
